import { Component, OnInit, ViewChild, AfterViewInit, TemplateRef, Optional } from '@angular/core'
import { BaseComponent } from '../../base.component';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActionService, PlacementService} from '../../_services';

import { CrmUserComponent } from '../../shared';
import { ClientDataHelper } from '../../_helpers/client-data-helper';
import { MatDialog } from '@angular/material/dialog';
import { TcDashboardComponent } from './tc-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { AuthenticationStateHelperService } from '../../_helpers/authentication-state-helper/authentication-state-helper.service';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { FilterSpec, FilterState } from '../../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../../shared/filter-ux/screen-filter-ux';
import { DatePreset, isDatePreset, resolveDatePreset, toIsoDate } from '../../shared/filter-ux/date-presets';
import { DatePresetSelection } from '../../shared/filter-ux/date-presets.component';

@Component({
    templateUrl: 'tc-dashboard.html',
    styleUrls: ['tc-dashboard.css'],
    standalone: false
})

export class CoordinatorDashboard extends BaseComponent implements OnInit, AfterViewInit {
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(CrmUserComponent, { static: false }) crmUser;
  @ViewChild('utilityBarForm', { static: true }) utilityBarForm: TemplateRef<any>;
  
  displayedColumns: string[] = ['user'];
  frmUserSearch: UntypedFormGroup;
  errorMsg: string = "";
  recordCount: number = 0;
  loading: boolean = false;
  noData: boolean = true;
  nodataStr: string = '';
  searchData: string = "dashboardSearch";

  private staffFilter: boolean = false;

  /** For the template's `*appFeatureSwitch`. */
  public readonly featureKeys = FeatureSwitchKeys;
  /** The filters.actions switch (shared with the actions screen). Off: no brand filter, no companyIds sent. */
  public filtersEnabled: boolean = false;
  /** Selected brands; empty means every brand the user may see. */
  public companyIds: number[] = [];
  /** The date preset behind Date From / Date To, or null when they were set by hand. */
  public datePreset: DatePreset | null = null;

  /**
   * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates
   * only: free text, names and emails stay out of the URL (browser history, telemetry) and browser storage.
   */
  private static readonly filterSpec: FilterSpec = { from: 'string', to: 'string', preset: 'string', brand: 'number[]' };
  /**
   * Remembered filters and filter state in the URL, while filters.actions is on. Remembered under its
   * own key: the actions screen shares the switch but not the filters.
   */
  private filterUx: ScreenFilterUx;
  private viewReady: boolean = false;
  private filterUxRestored: boolean = false;
  constructor(
    private formBuilder: UntypedFormBuilder,
    private placementService: PlacementService,
    private actionService: ActionService,
    private clientDataHelper: ClientDataHelper,
    public dialog: MatDialog,
    public router: Router,
    private featureHelperService: FeatureHelperService,
    private errorModalHelper: ErrorModalHelperService,
    private authenticationStateHelper: AuthenticationStateHelperService,
    private featureSwitches: FeatureSwitchService,
    @Optional() private route: ActivatedRoute = null
    ) {
    super();
  }

  ngOnInit() {
    
    let featureList = new Array<string>();
    featureList.push("TcDashboardView", "StaffFilter");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    if (!currentUserFeatures.filter(f => f.feature === "TcDashboardView")[0].enabled) {
      console.error("User does not have the required permissions to view the coordinator dashboard screen");
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname }});
      this.authenticationStateHelper.unauthorizedToken = true;
    }
    this.staffFilter = currentUserFeatures.filter(f => f.feature === "StaffFilter")[0].enabled;

    // Defaults: from = one month ago, to = today (native date inputs take yyyy-MM-dd strings).
    this.frmUserSearch = this.formBuilder.group({
      startDate: [new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10)],
      endDate: [new Date().toISOString().slice(0, 10)]
    });
    this.filterUx = new ScreenFilterUx(FeatureSwitchKeys.filtersActions + '.tc-dashboard', CoordinatorDashboard.filterSpec,
      this.router, this.route);
    this.featureSwitches.isEnabled(FeatureSwitchKeys.filtersActions).subscribe(enabled => {
      this.filtersEnabled = enabled;
      this.restoreFilterUx();
    });
    this.setClientData();
  }

  ngAfterViewInit() {
    let searchData = this.clientDataHelper.getData(this.searchData);
    if (searchData != null) {
      if (this.crmUser != null) {
        this.crmUser.UserData.subscribe({
          next: (n) =>
            this.setUserSessionData(searchData.user)
        });
      }
      else {
        this.setUserSessionData(searchData.user)
      }
    }
    this.viewReady = true;
    this.restoreFilterUx();
  }

  /**
   * With the switch on, a shared link's filters, else (when this tab has no last search) this user's
   * remembered ones, then the dashboard loads. Runs once both the switch and the view are ready: the
   * search needs the coordinator picker.
   */
  private restoreFilterUx() {
    if (this.filterUxRestored || !this.filtersEnabled || !this.viewReady || !this.crmUser) {
      return;
    }
    this.filterUxRestored = true;
    const hasTabSearch = this.clientDataHelper.getData(this.searchData) != null;
    const state = this.filterUx.urlState() ?? (hasTabSearch ? null : this.filterUx.rememberedState());
    if (state) {
      this.datePreset = isDatePreset(state.preset) ? state.preset : null;
      const range = this.datePreset ? resolveDatePreset(this.datePreset) : null;
      this.frmUserSearch.patchValue({
        startDate: range ? range.from : (state.from ?? this.frmUserSearch.get('startDate').value),
        endDate: range ? range.to : (state.to ?? this.frmUserSearch.get('endDate').value)
      });
      this.companyIds = (state.brand as number[]) ?? [];
      this.searchAction();
    }
  }

  onDatePreset(selection: DatePresetSelection) {
    this.datePreset = selection.preset;
    this.frmUserSearch.patchValue({ startDate: selection.from, endDate: selection.to });
  }

  /** Clear all: back to the last month, forgotten, and out of the URL. */
  clearAll() {
    this.frmUserSearch.reset({
      startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10),
      endDate: new Date().toISOString().slice(0, 10)
    });
    this.companyIds = [];
    this.datePreset = null;
    this.clientDataHelper.storeData(null, this.searchData);
    this.filterUx.clear();
  }

  /** A preset is remembered as the preset, so it resolves afresh on the next visit. */
  private filterState(): FilterState {
    const value = this.frmUserSearch.getRawValue();
    return {
      preset: this.datePreset,
      from: this.datePreset ? null : CoordinatorDashboard.isoDate(value.startDate),
      to: this.datePreset ? null : CoordinatorDashboard.isoDate(value.endDate),
      brand: this.companyIds
    };
  }

  /** The form holds a yyyy-MM-dd string until the user edits the date, then a Date. */
  private static isoDate(value: any): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : toIsoDate(date);
  }

  setUserSessionData(user) {
    this.crmUser.setUsers(user);
    this.searchAction();
  }

  searchAction() {
    this.initializeSearch();
    let params = this.frmUserSearch.value;
    params.user = this.crmUser.selectedAllUsers ? null : this.crmUser.frmCrmUser.value.user;
    params.userType = "Coordinator";
    // The brand filter is new UI behind filters.actions; off, the request carries no companyIds.
    if (this.filtersEnabled) {
      params.companyIds = this.companyIds;
    }
    this.clientDataHelper.storeData(params, this.searchData);
    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }
    this.actionService.getActionsSearch(params, this.staffFilter).subscribe(data => {
      this.filterData(data);
    }, error => {
        this.errorModalHelper.show(error.message, error);
    });
  }

  filterData(data) {
    this.nodataStr = '';
    let result = data.map(a => a.placementId);
    if (result.length === 0) {
      this.setNoData();
      return;
    }
    var crmUser = [];
    var params: any = {};
    params.placementIds = result;
    params.relationShipId = 9;
    let user = this.crmUser.selectedAllUsers ? null : this.crmUser.frmCrmUser.value.user;

    if (user !== null) {
      user.forEach((el) => {
        crmUser.push({ email: el.email, userRelationshipId: el.userRelationshipId });
      });
    }
    params.user = crmUser;
    this.placementService.GetUsersByPlacementIds(params).subscribe((result: any) => {
      if (result.length > 0) {
        let a = 0;
        result.forEach(x => {
          let highlightUser = false;
          let i = 0;
          let tmpUserActions = [];
          x.user.forEach(y => {
            let actionData = data.filter(d => d.placementId === y.placementID);
            tmpUserActions.push(actionData);
            i += actionData.length;
            if (actionData.length > 0) {
              actionData.forEach(d => {
                if (d.actionType.statusId === 160 || d.actionType.statusId === 170) {
                  highlightUser = true;
                }
              })
            }
          })
          result[a].actions = i;
          result[a].highlightUser = highlightUser;
          result[a].userActions = tmpUserActions;
          a += 1;
        });
      }
      let tmpResult = [];
      let highlightedResult = result.filter(h => h.highlightUser);
      if (highlightedResult.length > 0) {
        highlightedResult = highlightedResult.sort(function (x, y) {
          return (y.actions - x.actions);
        });
        highlightedResult.forEach(x => {
          tmpResult.push(x);
        })
      }

      let nonHighlightedResult = result.filter(h => !h.highlightUser)
      if (nonHighlightedResult.length > 0) {
        nonHighlightedResult = nonHighlightedResult.sort(function (x, y) {
          return (y.actions - x.actions);
        });
        nonHighlightedResult.forEach(x => {
          tmpResult.push(x);
        })
      }
      this.dataSource = new MatTableDataSource(tmpResult);
      this.noData = false;
      if (this.dataSource.data.length === 0) {
        this.setNoData();
      }
    }, error => {
      this.errorModalHelper.show(error.message, error);
    })
  }

  onDataclicked(element) {
    this.openDialog(element);
  }

  setNoData() {
    this.nodataStr = 'No Results Found';
    this.noData = true;
  }

  initializeSearch() {
    this.dataSource = new MatTableDataSource(null);
    this.noData = true;
  }

  setClientData() {
    let searchData = this.clientDataHelper.getData(this.searchData);
    if (searchData != null) {
      this.frmUserSearch.get("startDate").setValue(searchData.startDate);
      this.frmUserSearch.get("endDate").setValue(searchData.endDate);
      this.companyIds = searchData.companyIds ?? [];
    }
  }

  private openDialog(data) {
    const dialogRef = this.dialog.open(TcDashboardComponent, {
      data: { data },
      panelClass: 'my-dialog'
    });
  }
}
